// --- Interfaces ---
export interface HotelInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  star_rating: number;
  num_rooms: number;
  year_built: number;
  lot_size: number;
  description: string;
  website: string;
  notes: string;
  amenities: string[];
  property_type?: string;
  chain_affiliation?: string;
}

export interface Metric {
  category: string;
  item: string;
  value: string | number;
  year: number;
  source?: 'confirmed' | 'manual' | 'ocr';
  readOnly?: boolean;
}

export interface DealData {
  name: string;
  asking_price: string;
  hotelInfo: HotelInfo;
  metrics: Metric[];
}

export interface AiInsights {
  investmentRisk: 'Low' | 'Medium' | 'High';
  profitabilityScore: number;
  valuationEstimate: {
    low: number;
    high: number;
  };
  summary: string;
  keyMetrics: {
    totalRevenue: number;
    totalExpenses: number;
    netOperatingIncome: number;
    profitMargin: number;
    occupancyRate?: number;
    adr?: number;
    revpar?: number;
  };
}

export interface ExistingHotel {
  id: string;
  name: string;
  city: string;
  state: string;
  num_rooms: number;
  star_rating: number;
  address: string;
  zip_code: string;
  country: string;
  year_built: number;
  lot_size: number;
  description: string;
  website: string;
  notes: string;
  amenities: string[];
  property_type?: string;
  chain_affiliation?: string;
}

export interface OCRCategory {
  id: number;
  name: string;
  category_group: string;
}

export interface OCRFinancialData {
  categories: OCRCategory[];
  years: string[];
  data: {
    [categoryId: number]: {
      item_name: string;
      values: {
        [year: string]: number;
      };
      file_id?: string;
    }[];
  };
}

export interface CategoryData {
  order: number;
  items: string[];
}

export interface MetricGroupedCategories {
  [key: string]: CategoryData;
}

export interface EditedData {
  [categoryId: number]: {
    [itemName: string]: {
      [year: string]: string;
    };
  };
}

// Constants
export const steps = ['Hotel Selection', 'Financial Data', 'Review & Analyze', 'AI Valuation Insights', 'Save Deal'];

export const metricGroupedCategories: MetricGroupedCategories = {
  Revenue: {
    order: 1,
    items: [
      'Room Revenue',
      'F&B Revenue',
      'Other Revenue',
      'Total Revenue'
    ],
  },
  Expenses: {
    order: 2,
    items: [
      'Administrative & General',
      'Sales & Marketing',
      'Property Operations & Maintenance',
      'Utilities',
      'Room Expenses',
      'F&B Expenses',
      'Total Expenses'
    ],
  },
  'Performance Metrics': {
    order: 3,
    items: [
      'RevPAR',
      'ADR',
      'Occupancy Rate',
      'GOP',
      'NOI',
      'EBITDA'
    ],
  },
  Other: {
    order: 4,
    items: [
      'Cap Rate',
      'Price per Key',
      'Market Penetration Index'
    ],
  },
};

// Helper Functions
export const validateMetricValue = (value: string | number): boolean => {
  const numValue = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : value;
  return !isNaN(numValue);
};

export const formatMetricValue = (value: string | number): string => {
  return typeof value === 'string' ? value : value.toString();
};